import { z } from '@/domain/validation/zod';
import { create } from 'zustand';
import { getPreferencesRepository } from '@/repositories/preferencesRepository';

/**
 * Device preferences for editing (docs/04: nudge distances are editable preferences;
 * snapping and the grid have visible toggles). Stored in this browser, not in projects
 * or backups. Values read back from storage are validated field by field, so a bad or
 * older value falls back to its default instead of breaking the studio.
 */
export const editorPreferencesSchema = z.object({
  /** Arrow-key nudge in millimeters. */
  nudgeMm: z.number().min(0.01).max(10),
  /** Shift+arrow nudge in millimeters. */
  nudgeLargeMm: z.number().min(0.05).max(50),
  snapEnabled: z.boolean(),
  snapToObjects: z.boolean(),
  /** Artboard and nail outline edges and centers. */
  snapToArtboard: z.boolean(),
  snapToGuides: z.boolean(),
  snapToGrid: z.boolean(),
  gridVisible: z.boolean(),
  gridSpacingMm: z.number().min(0.1).max(20),
});

export type EditorPreferences = z.infer<typeof editorPreferencesSchema>;

export const DEFAULT_EDITOR_PREFERENCES: EditorPreferences = {
  nudgeMm: 0.1,
  nudgeLargeMm: 1,
  snapEnabled: true,
  snapToObjects: true,
  snapToArtboard: true,
  snapToGuides: true,
  snapToGrid: false,
  gridVisible: false,
  gridSpacingMm: 1,
};

export const EDITOR_PREFERENCES_KEY = 'editor';

/** Keeps every stored field that is still valid; everything else gets its default. */
export function readEditorPreferences(raw: unknown): EditorPreferences {
  const result: Record<string, unknown> = { ...DEFAULT_EDITOR_PREFERENCES };
  if (raw && typeof raw === 'object') {
    const shape = editorPreferencesSchema.shape;
    for (const key of Object.keys(shape) as (keyof EditorPreferences)[]) {
      const parsed = shape[key].safeParse((raw as Record<string, unknown>)[key]);
      if (parsed.success) result[key] = parsed.data;
    }
  }
  return result as EditorPreferences;
}

interface PreferencesState {
  readonly editor: EditorPreferences;
  readonly loaded: boolean;
  load(): Promise<void>;
  /** Applies valid fields immediately and saves them on this device (best effort). */
  update(patch: Partial<EditorPreferences>): void;
  reset(): void;
}

export const usePreferences = create<PreferencesState>((set, get) => ({
  editor: DEFAULT_EDITOR_PREFERENCES,
  loaded: false,
  async load() {
    try {
      const raw = await getPreferencesRepository().get(EDITOR_PREFERENCES_KEY);
      set({ editor: readEditorPreferences(raw), loaded: true });
    } catch {
      set({ loaded: true }); // storage unavailable: defaults for this session
    }
  },
  update(patch) {
    const editor = readEditorPreferences({ ...get().editor, ...patch });
    set({ editor });
    void getPreferencesRepository()
      .set(EDITOR_PREFERENCES_KEY, editor)
      .catch(() => {
        // Preferences are conveniences; failing to store them never blocks editing.
      });
  },
  reset() {
    get().update(DEFAULT_EDITOR_PREFERENCES);
  },
}));
